/**
 * PatientReportWizard — one-pager builder for exporting reports
 * (ייצוא דוחות). Sections reveal progressively:
 *
 *   1 סוג הדוח · [2 בחירת מטופל — only for patient-scope reports]
 *   · טווח תאריכים · נתונים להצגה · תצוגה וייצוא
 *
 * Clinic-scope reports (e.g. סקירת כל המטופלים) skip the patient step
 * entirely; section numbers adjust. Everything type-specific (scope, columns,
 * extra datasets, row building) comes from reportTypeRegistry.
 *
 * PDF generation happens server-side (generatePatientReportPdf callable) and
 * every export is persisted to Storage + the generatedReports history.
 *
 * Error codes: CK-3720 (export flow).
 */

import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ArrowCounterClockwise } from "@phosphor-icons/react";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../../../store/slices/authSlice";
import { useGetPatientsQuery } from "../../../../store/services/patientsApi";
import { useGetAppointmentsQuery } from "../../../../store/services/calendarApi";
import { useGetTransactionsInRangeQuery } from "../../../../store/services/financeApi";
import {
  useGetTreatmentsInRangeQuery,
  useGeneratePatientReportPdfMutation,
} from "../../../../store/services/patientReportsApi";
import { logActivity, ACTIVITY_ACTIONS, ENTITY_TYPES } from "../../../../firebase";
import { formatHebrewDate } from "../../../../utils/dateUtils";
import usePatientReportWizard from "./usePatientReportWizard";
import Section from "./Section";
import PdfReadyCard from "./PdfReadyCard";
import PdfPreviewModal from "./PdfPreviewModal";
import FinanceReportFlow from "./finance/FinanceReportFlow";
import PatientSelectStep from "./steps/PatientSelectStep";
import ReportTypeStep from "./steps/ReportTypeStep";
import DateRangeStep from "./steps/DateRangeStep";
import ColumnsStep from "./steps/ColumnsStep";
import PreviewStep from "./steps/PreviewStep";

const CLINIC_REPORT_LABEL = "כל המטופלים";

function PatientReportWizard({
  clinicId,
  scopeTherapistId,
  canAccessFinance,
  canExportData,
}) {
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);

  const wiz = usePatientReportWizard({ canAccessFinance });
  const {
    reset,
    needsPatient,
    patientId,
    reportType,
    visibleColumns,
    enabledColumns,
    startDate,
    endDate,
    patientOk,
    typeOk,
    rangeOk,
    columnsOk,
    isReady,
    isFinanceFlow,
  } = wiz;

  // Progressive disclosure — dates unlock once type (and patient, when
  // required) are chosen; columns after dates; preview when everything holds.
  // The finance flow replaces dates/columns/preview with its own sections.
  const datesEnabled = !isFinanceFlow && typeOk && patientOk;
  const columnsEnabled = datesEnabled && rangeOk;
  const previewEnabled = isReady;

  // Dynamic numbering: the patient step only exists for patient-scope reports.
  const nums = needsPatient
    ? { patient: 2, dates: 3, columns: 4, preview: 5 }
    : { dates: 2, columns: 3, preview: 4 };

  // ─── Patients (scoped like the rest of the reports page) ───
  const { data: patientsData, isLoading: patientsLoading } =
    useGetPatientsQuery(
      {
        clinicId,
        therapistId: scopeTherapistId || undefined,
        pageSize: 500,
        includeInactive: true,
      },
      { skip: !clinicId }
    );
  const patients = useMemo(
    () => patientsData?.patients || [],
    [patientsData]
  );
  const patient = patients.find((p) => p.id === patientId) || null;
  const patientName = needsPatient
    ? patient
      ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
      : ""
    : CLINIC_REPORT_LABEL;

  // ─── Report data — fetched only once every section is valid ───
  const previewGate = !clinicId || !isReady;

  const {
    data: appointments,
    isLoading: appointmentsLoading,
    error: appointmentsError,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      startDate: `${startDate}T00:00:00`,
      endDate: `${endDate}T23:59:59`,
      // Clinic-scope reports pull the whole clinic (therapist scope applies)
      patientId: needsPatient ? patientId : undefined,
      therapistId: needsPatient ? undefined : scopeTherapistId || undefined,
    },
    { skip: previewGate }
  );

  const financeColumnsOn = visibleColumns.some(
    (c) => c.finance && enabledColumns[c.key]
  );
  const {
    data: transactions,
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useGetTransactionsInRangeQuery(
    {
      clinicId,
      start: `${startDate}T00:00:00`,
      end: `${endDate}T23:59:59`,
      therapistId: scopeTherapistId || undefined,
    },
    { skip: previewGate || !financeColumnsOn }
  );

  const needsTreatments = reportType?.dataNeeds?.treatments === true;
  const {
    data: treatments,
    isLoading: treatmentsLoading,
    error: treatmentsError,
  } = useGetTreatmentsInRangeQuery(
    {
      clinicId,
      start: `${startDate}T00:00:00`,
      end: `${endDate}T23:59:59`,
      therapistId: scopeTherapistId || undefined,
    },
    { skip: previewGate || !needsTreatments }
  );

  // ─── Compile rows via the registry entry ───
  const enabledColumnDefs = useMemo(
    () => visibleColumns.filter((c) => enabledColumns[c.key]),
    [visibleColumns, enabledColumns]
  );

  const rows = useMemo(() => {
    if (!reportType || !appointments) return [];
    // getAppointments drops the therapist filter when patientId is set —
    // re-apply the page-level therapist scope client-side.
    const scoped =
      needsPatient && scopeTherapistId
        ? appointments.filter((a) => a.therapistId === scopeTherapistId)
        : appointments;
    return reportType.buildRows({
      appointments: scoped,
      transactions: transactions || [],
      treatments: treatments || [],
      patient,
      patients,
    });
  }, [
    reportType,
    appointments,
    transactions,
    treatments,
    patient,
    patients,
    needsPatient,
    scopeTherapistId,
  ]);

  // ─── Export ───
  const [generatePdf, { isLoading: isExporting }] =
    useGeneratePatientReportPdfMutation();
  const [exportError, setExportError] = useState(null);
  const [lastPdfUrl, setLastPdfUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleReset = useCallback(() => {
    setExportError(null);
    setLastPdfUrl(null);
    setPreviewOpen(false);
    reset();
  }, [reset]);

  const handleExport = useCallback(async () => {
    setExportError(null);
    setLastPdfUrl(null);
    setPreviewOpen(false);
    // Stay in CliniKit while rendering; the in-app preview opens once the
    // URL is ready (no popup → nothing to block).
    try {
      const payload = {
        clinicId,
        patientId: needsPatient ? patientId : null,
        patientName,
        reportTypeId: reportType.id,
        reportTypeLabel: reportType.label,
        rangeStart: startDate,
        rangeEnd: endDate,
        columns: enabledColumnDefs.map(({ key, label }) => ({ key, label })),
        rows: rows.map((row) => {
          const out = {};
          enabledColumnDefs.forEach(({ key }) => {
            out[key] = row[key] ?? "—";
          });
          return out;
        }),
      };
      const result = await generatePdf(payload).unwrap();

      setLastPdfUrl(result.pdfUrl);
      setPreviewOpen(true);

      logActivity(clinicId, {
        therapistId,
        therapistName: therapistName || "לא ידוע",
        action: ACTIVITY_ACTIONS.GENERATE_PATIENT_REPORT,
        entityType: ENTITY_TYPES.PATIENT,
        entityId: needsPatient ? patientId : clinicId,
        entityName: patientName,
        details: {
          reportTypeId: reportType.id,
          rangeStart: startDate,
          rangeEnd: endDate,
          rowCount: rows.length,
        },
      });
    } catch (error) {
      console.error("[CK-3720] Error generating patient report PDF:", {
        errorCode: "CK-3720",
        errorMessage: error?.message,
        clinicId,
        reportTypeId: reportType?.id,
        timestamp: new Date().toISOString(),
      });
      setExportError("אירעה שגיאה בהפקת הדוח. נסו שוב בעוד רגע.");
    }
  }, [
    clinicId,
    needsPatient,
    patientId,
    patientName,
    reportType,
    startDate,
    endDate,
    enabledColumnDefs,
    rows,
    generatePdf,
    therapistId,
    therapistName,
  ]);

  const rangeSummary =
    rangeOk && startDate && endDate
      ? `${formatHebrewDate(startDate)} – ${formatHebrewDate(endDate)}`
      : null;

  return (
    <div className="prw-onepager">
      <div className="prw-row">
        <Section
          num={1}
          title="סוג הדוח"
          done={typeOk}
          summary={typeOk ? reportType?.label : null}
        >
          <ReportTypeStep wiz={wiz} canAccessFinance={canAccessFinance} />
        </Section>

        {needsPatient && typeOk && (
          <Section
            num={nums.patient}
            title="בחירת מטופל"
            done={!!patientId}
            summary={patient ? patientName : null}
          >
            <PatientSelectStep
              wiz={wiz}
              patients={patients}
              isLoading={patientsLoading}
            />
          </Section>
        )}
      </div>

      {isFinanceFlow && canAccessFinance && (
        <FinanceReportFlow
          wiz={wiz}
          clinicId={clinicId}
          scopeTherapistId={scopeTherapistId}
          canExportData={canExportData}
          therapistId={therapistId}
          therapistName={therapistName}
          startNum={2}
          onReset={handleReset}
        />
      )}

      {datesEnabled && (
        <div className="prw-row">
          <Section
            num={nums.dates}
            title="טווח תאריכים"
            done={rangeOk}
            summary={rangeSummary}
          >
            <DateRangeStep wiz={wiz} />
          </Section>

          {columnsEnabled && (
            <Section
              num={nums.columns}
              title="נתונים להצגה"
              done={columnsOk}
              summary={columnsOk ? `${enabledColumnDefs.length} עמודות` : null}
            >
              <ColumnsStep wiz={wiz} />
            </Section>
          )}
        </div>
      )}

      {previewEnabled && (
        <Section num={nums.preview} title="תצוגה מקדימה וייצוא" done={false}>
          <PreviewStep
            wiz={wiz}
            columns={enabledColumnDefs}
            rows={rows}
            isLoadingData={
              appointmentsLoading ||
              (financeColumnsOn && transactionsLoading) ||
              (needsTreatments && treatmentsLoading)
            }
            dataError={appointmentsError || transactionsError || treatmentsError}
            canExportData={canExportData}
            isExporting={isExporting}
            exportError={exportError}
            onExport={handleExport}
            patientName={patientName}
          />
          <PdfReadyCard url={lastPdfUrl} onPreview={() => setPreviewOpen(true)} />
          <button
            type="button"
            className="prw-btn ghost prw-reset-btn"
            onClick={handleReset}
            disabled={isExporting}
            title="התחלת דוח חדש"
          >
            <ArrowCounterClockwise size={15} />
            דוח חדש
          </button>
        </Section>
      )}

      {previewOpen && lastPdfUrl && (
        <PdfPreviewModal
          url={lastPdfUrl}
          title={reportType?.label || "דוח"}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

export default PatientReportWizard;
